import { BrowserProvider } from 'ethers';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import EthereumProvider from '@walletconnect/ethereum-provider';

// WalletConnect Project ID - Get yours at https://cloud.walletconnect.com
const WALLETCONNECT_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;

// Supported chains
export const SUPPORTED_CHAINS = {
  1: {
    name: 'Ethereum',
    chainId: 1,
    rpcUrl: 'https://eth.llamarpc.com',
    blockExplorer: 'https://etherscan.io',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }
  }
  // ,137: {
  //   name: 'Polygon',
  //   chainId: 137,
  //   rpcUrl: 'https://polygon-rpc.com',
  //   blockExplorer: 'https://polygonscan.com',
  //   nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 }
  // }
};

// Global WalletConnect provider instance
let walletConnectProvider = null;

// Check if MetaMask wallet is available
export const isWalletAvailable = () => {
  return typeof window.ethereum !== 'undefined';
};

// Detect if user is on mobile
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Initialize WalletConnect provider
export const initWalletConnect = async () => {
  try {
    // Always create a completely fresh provider instance
    // Don't reuse the global variable to avoid any cached sessions
    const newProvider = await EthereumProvider.init({
      projectId: WALLETCONNECT_PROJECT_ID,
      chains: [1], // Ethereum mainnet - only include actually supported chains
      // Removed optionalChains to prevent provider index mismatch errors
      // that cause "undefined is not an object (evaluating 'this.getProvider(i).setDefaultChain')"
      showQrModal: true, // Show QR code modal
      qrModalOptions: {
        themeMode: 'light',
        themeVariables: {
          '--wcm-z-index': '9999'
        }
      },
      methods: ['eth_sendTransaction', 'personal_sign'],
      // Don't subscribe to chainChanged events - they cause internal WalletConnect errors
      // when the provider tries to call setDefaultChain on undefined internal providers
      events: ['accountsChanged'],
      metadata: {
        name: 'CoinCommons',
        description: 'Decentralized address comments',
        url: window.location.origin,
        icons: []
      }
    });

    walletConnectProvider = newProvider;
    return newProvider;
  } catch (error) {
    console.error('Error initializing WalletConnect:', error);
    throw error;
  }
};

// Connect with WalletConnect
export const connectWalletConnect = async (forceAccountSelection = true) => {
  try {
    // Always clear WalletConnect cache to force fresh connection every time
    // This ensures users always see account selection and prevents confusion

    // Disconnect existing provider if any
    if (walletConnectProvider) {
      try {
        await walletConnectProvider.disconnect();
      } catch (error) {
        console.error('Error disconnecting provider:', error);
      }
      walletConnectProvider = null;
    }

    // Clear all WalletConnect data from localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      const wcKeys = Object.keys(window.localStorage).filter(key =>
        key.startsWith('wc@2') || key.startsWith('walletconnect') || key.includes('wc_')
      );
      wcKeys.forEach(key => {
        window.localStorage.removeItem(key);
      });
    }

    // Also clear sessionStorage
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const wcKeys = Object.keys(window.sessionStorage).filter(key =>
        key.startsWith('wc@2') || key.startsWith('walletconnect') || key.includes('wc_')
      );
      wcKeys.forEach(key => {
        window.sessionStorage.removeItem(key);
      });
    }

    // Clear IndexedDB WalletConnect databases
    if (typeof window !== 'undefined' && window.indexedDB) {
      try {
        const dbs = await window.indexedDB.databases();
        for (const db of dbs) {
          if (db.name && (db.name.includes('walletconnect') || db.name.includes('wc_'))) {
            window.indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (error) {
        // Could not clear IndexedDB
      }
    }

    // Small delay to ensure cleanup completes
    await new Promise(resolve => setTimeout(resolve, 100));

    const provider = await initWalletConnect();

    // Check for any existing pairings and disconnect them to force QR code
    if (provider.session) {
      try {
        await provider.disconnect();
        // Reinitialize after disconnecting
        const freshProvider = await initWalletConnect();

        const accounts = await freshProvider.enable();
        const address = accounts[0];

        // Get chain ID
        const chainId = freshProvider.chainId;

        // Check if chain is supported
        if (!SUPPORTED_CHAINS[chainId]) {
          throw new Error(`Unsupported network. Please switch to Ethereum or Polygon.`);
        }

        // Wrap with ethers provider
        const ethersProvider = new BrowserProvider(freshProvider);

        return {
          provider: ethersProvider,
          address: address.toLowerCase(),
          chainId,
          connectionType: 'walletconnect',
          rawProvider: freshProvider
        };
      } catch (error) {
        console.error('Error disconnecting existing session:', error);
        // Continue anyway
      }
    }

    // Enable session (triggers QR Code modal or deep link)
    const accounts = await provider.enable();
    const address = accounts[0];

    // Get chain ID
    const chainId = provider.chainId;

    // Check if chain is supported
    if (!SUPPORTED_CHAINS[chainId]) {
      throw new Error(`Unsupported network. Please switch to Ethereum or Polygon.`);
    }

    // Wrap with ethers provider
    const ethersProvider = new BrowserProvider(provider);

    return {
      provider: ethersProvider,
      address: address.toLowerCase(),
      chainId,
      connectionType: 'walletconnect',
      rawProvider: provider
    };
  } catch (error) {
    console.error('WalletConnect connection error:', error);
    throw error;
  }
};

// Request permissions to force account selection
export const requestAccountSelection = async () => {
  try {
    if (!isWalletAvailable()) {
      throw new Error('No Web3 wallet detected. Please install MetaMask or another Web3 wallet.');
    }

    // Force MetaMask to show account selector
    await window.ethereum.request({
      method: 'wallet_requestPermissions',
      params: [{ eth_accounts: {} }],
    });

    // Now get the selected account
    const provider = new BrowserProvider(window.ethereum);
    const accounts = await provider.send("eth_requestAccounts", []);
    return accounts;
  } catch (error) {
    console.error('Account selection error:', error);
    throw error;
  }
};

// Connect wallet and get address
export const connectWallet = async (forceAccountSelection = false) => {
  try {
    if (!isWalletAvailable()) {
      throw new Error('No Web3 wallet detected. Please install MetaMask or another Web3 wallet.');
    }

    const provider = new BrowserProvider(window.ethereum);
    let accounts;

    if (forceAccountSelection) {
      // Force account selection by requesting permissions again
      try {
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch (error) {
        // User rejected or error occurred
        if (error.code === 4001) {
          throw new Error('Account selection was cancelled');
        }
        // Continue anyway with existing account
      }
    }

    // Request account access
    try {
      accounts = await provider.send("eth_requestAccounts", []);
    } catch (error) {
      // Handle MetaMask-specific errors
      if (error.code === 4001) {
        throw new Error('Connection request was rejected');
      } else if (error.code === -32002) {
        throw new Error('Connection request is already pending. Please check MetaMask.');
      } else if (error.message?.includes('User rejected')) {
        throw new Error('Connection was rejected');
      } else {
        // Generic error handling
        console.error('MetaMask connection error:', error);
        throw new Error('Failed to connect to MetaMask. Please ensure it is unlocked and try again.');
      }
    }
    const address = accounts[0];

    // Get chain ID
    const network = await provider.getNetwork();
    const chainId = Number(network.chainId);

    // Check if chain is supported
    if (!SUPPORTED_CHAINS[chainId]) {
      throw new Error(`Unsupported network. Please switch to Ethereum or Polygon.`);
    }

    return {
      provider,
      address: address.toLowerCase(),
      chainId,
      connectionType: 'metamask'
    };
  } catch (error) {
    console.error('Wallet connection error:', error);
    throw error;
  }
};

// Generate cryptographically secure nonce
const generateSecureNonce = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Create SIWE (Sign-In with Ethereum) message
// Follows EIP-4361 standard with security enhancements
export const createSIWEMessage = (address, chainId) => {
  const origin = window.location.origin;
  const domain = window.location.host;
  const nonce = generateSecureNonce(); // 32-char hex (128-bit)
  const issuedAt = new Date().toISOString();
  const expirationTime = new Date(Date.now() + 5 * 60 * 1000).toISOString(); // 5 minutes

  return {
    message: `${domain} wants you to sign in with your Ethereum account:
${address}

Sign in to CoinCommons

URI: ${origin}
Version: 1
Chain ID: ${chainId}
Nonce: ${nonce}
Issued At: ${issuedAt}
Expiration Time: ${expirationTime}`,
    nonce,
    issuedAt,
    expirationTime
  };
};

// Sign authentication message
export const signAuthMessage = async (provider, address, chainId) => {
  try {
    const signer = await provider.getSigner();

    const { message, nonce, issuedAt } = createSIWEMessage(address, chainId);

    // Request signature from user's wallet
    const signature = await signer.signMessage(message);

    return {
      signature,
      message,
      nonce,
      issuedAt
    };
  } catch (error) {
    console.error('Signature error:', error);
    if (error.code === 4001) {
      throw new Error('Signature request rejected by user');
    }
    throw error;
  }
};

// ============================================================================
// DEPRECATED: Legacy User Profile Functions
// ============================================================================
// These functions are deprecated after the multi-chain migration.
// The 'users' collection no longer exists. User identity is now represented
// by composite IDs (e.g., "ethereum_0x1234...") and stored in Firebase Auth.
// Use getCompositeId() and related functions instead.
// ============================================================================

/**
 * @deprecated After multi-chain migration, use getCompositeId() instead.
 * The 'users' collection no longer exists.
 */
/**
 * Get user profile data from the addresses collection
 * @param {string} compositeId - The address (chain-agnostic UID, e.g., "0x1234..." or "1A1zP1...")
 * @returns {Promise<Object|null>} Profile data with registeredAt and lastSeenAt
 */
export const getUserProfile = async (compositeId) => {
  if (!compositeId) return null;

  try {
    const { getDoc } = await import('firebase/firestore');
    const addressRef = doc(db, 'addresses', compositeId);
    const addressDoc = await getDoc(addressRef);

    if (!addressDoc.exists()) {
      return null;
    }

    const data = addressDoc.data();
    return {
      registeredAt: data.registeredAt || null,
      lastSeenAt: data.lastSeenAt || null,
      username: data.username || null
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

/**
 * Get display info for an email user (username and displayName)
 * Used by Comment component to show proper author names
 * @param {string} emailCompositeId - Email user's compositeId (e.g., "e_L6aJZV...")
 * @returns {Promise<{username: string|null, displayName: string|null}>}
 */
export const getEmailUserDisplayInfo = async (emailCompositeId) => {
  if (!emailCompositeId || !emailCompositeId.startsWith('e_')) {
    return { username: null, displayName: null };
  }

  try {
    const { getDoc } = await import('firebase/firestore');
    const addressRef = doc(db, 'addresses', emailCompositeId);
    const addressDoc = await getDoc(addressRef);

    if (!addressDoc.exists()) {
      return { username: null, displayName: null };
    }

    const data = addressDoc.data();
    return {
      username: data.username || null,
      displayName: data.displayName || null
    };
  } catch (error) {
    console.error('Error fetching email user display info:', error);
    return { username: null, displayName: null };
  }
};

/**
 * @deprecated After multi-chain migration, user profiles are not stored separately.
 * User identity is managed through Firebase Auth with composite IDs.
 */
export const saveUserProfile = async (address, chainId) => {
  console.warn('[DEPRECATED] saveUserProfile is deprecated.');
  console.warn('[DEPRECATED] User profiles are no longer stored after multi-chain migration.');
  return null;
};

// ============================================================================
// Multi-Chain User Identity Functions
// ============================================================================

/**
 * Get the current user's composite ID from localStorage
 * @returns {string|null} Composite ID (e.g., "ethereum_0x1234...") or null if not authenticated
 */
export const getCompositeId = () => {
  return localStorage.getItem('wallet_composite_id');
};

/**
 * Get the current user's chain
 * @returns {string|null} Chain name (e.g., "ethereum", "bsc") or null if not authenticated
 */
export const getChain = () => {
  return localStorage.getItem('wallet_chain');
};

/**
 * Get the current user's address (without chain prefix)
 * @returns {string|null} Address or null if not authenticated
 */
export const getAddress = () => {
  return localStorage.getItem('wallet_address');
};

/**
 * Clear all wallet-related data from localStorage
 */
export const clearWalletData = () => {
  localStorage.removeItem('wallet_composite_id');
  localStorage.removeItem('wallet_chain');
  localStorage.removeItem('wallet_address');
  localStorage.removeItem('coincommons_user'); // Clear user session data
};

// Complete wallet authentication flow with Firebase Auth integration
export const authenticateWithWallet = async (connectionMethod = 'metamask', forceAccountSelection = true, turnstileToken = null) => {
  try {
    // Import Firebase services
    const { auth, functions } = await import('./firebase');
    const { signInWithCustomToken } = await import('firebase/auth');
    const { httpsCallable } = await import('firebase/functions');

    // Step 1: Connect wallet based on method
    let connectionResult;
    if (connectionMethod === 'walletconnect') {
      connectionResult = await connectWalletConnect(forceAccountSelection);
    } else {
      connectionResult = await connectWallet(forceAccountSelection);
    }

    const { provider, address, chainId, connectionType, rawProvider } = connectionResult;

    // Step 2: Sign SIWE authentication message
    const { signature, message } = await signAuthMessage(provider, address, chainId);

    // Step 3: Call Firebase Function to verify signature and get custom token
    const verifyWalletSignature = httpsCallable(functions, 'verifyWalletSignature');
    const { data } = await verifyWalletSignature({
      signature,
      message,
      turnstileToken
    });

    // Step 4: Verify backend returned the same address (security check)
    if (data.address.toLowerCase() !== address.toLowerCase()) {
      throw new Error('Address mismatch - possible attack detected!');
    }

    // Extract normalized address and chain from backend response
    // UID is now just the address (chain-agnostic)
    const { address: normalizedAddress, chain } = data;

    // Step 5: Sign in to Firebase with custom token
    // This sets request.auth.uid = normalizedAddress (e.g., "0x1234...") in Firestore rules
    await signInWithCustomToken(auth, data.customToken);

    // Step 6: Track user registration and sign-in timestamps
    // registeredAt indicates this is a registered user (not just an org address)
    const { getDoc } = await import('firebase/firestore');
    const addressRef = doc(db, 'addresses', normalizedAddress);
    const addressDoc = await getDoc(addressRef);

    const updateData = {
      lastSeenAt: serverTimestamp()
    };

    // Set registeredAt only on first sign-in (if document doesn't exist or field is missing)
    if (!addressDoc.exists() || !addressDoc.data()?.registeredAt) {
      updateData.registeredAt = serverTimestamp();
    }

    await setDoc(addressRef, updateData, { merge: true });

    // Fetch the updated profile data to return
    const updatedDoc = await getDoc(addressRef);
    const profile = updatedDoc.exists() ? {
      registeredAt: updatedDoc.data().registeredAt,
      lastSeenAt: updatedDoc.data().lastSeenAt
    } : null;

    // Step 7: Store normalized address in localStorage for app use
    // Note: User identity is now chain-agnostic - just the address
    // UID = normalizedAddress (e.g., "0x1234..." for EVM, "1A1zP1..." for Bitcoin)
    localStorage.setItem('wallet_composite_id', normalizedAddress);
    localStorage.setItem('wallet_chain', chain);
    localStorage.setItem('wallet_address', normalizedAddress);

    return {
      address: normalizedAddress,
      chainId,
      chain,
      compositeId: normalizedAddress, // For backward compatibility
      signature,
      connectionType,
      rawProvider,
      profile
    };
  } catch (error) {
    console.error('Authentication error:', error);

    // Track authentication errors to Sentry (except user rejections)
    if (!error.message?.includes('User rejected') 
      && !error.message?.includes('User denied')
      && !error.message?.includes('cancelled')) {
      const { trackAuthError } = await import('./utils/errorTracking');
      trackAuthError(error, connectionMethod, {
        errorCode: error.code,
        errorMessage: error.message
      });
    }

    // Provide user-friendly error messages
    if (error.code === 'functions/unauthenticated') {
      throw new Error('Authentication failed. Please try signing the message again.');
    } else if (error.code === 'functions/resource-exhausted') {
      throw new Error('Too many authentication attempts. Please try again later.');
    } else if (error.message?.includes('User rejected')) {
      throw new Error('Signature request rejected by user');
    }

    throw error;
  }
};

// Disconnect wallet (clear local state and Firebase auth)
export const disconnectWallet = async () => {
  try {
    // Import Firebase auth
    const { auth } = await import('./firebase');
    const { signOut } = await import('firebase/auth');

    // Sign out from Firebase Auth
    await signOut(auth);

    // Clear wallet data from localStorage
    clearWalletData();

    // Disconnect WalletConnect if active
    if (walletConnectProvider) {
      await walletConnectProvider.disconnect();
      walletConnectProvider = null;
    }

    // Note: Can't truly "disconnect" from MetaMask browser extension
    // This just clears our local state and Firebase session
  } catch (error) {
    console.error('Error disconnecting wallet:', error);
  }
};

// Listen for account changes
export const onAccountsChanged = (callback) => {
  const accountsChangedHandler = (accounts) => {
    if (accounts.length === 0) {
      // User disconnected all accounts
      callback(null);
    } else {
      // User switched accounts
      callback(accounts[0].toLowerCase());
    }
  };

  const disconnectHandler = () => {
    callback(null);
  };

  // MetaMask events
  if (window.ethereum) {
    window.ethereum.on('accountsChanged', accountsChangedHandler);
  }

  // WalletConnect events
  if (walletConnectProvider) {
    walletConnectProvider.on('accountsChanged', accountsChangedHandler);
    walletConnectProvider.on('disconnect', disconnectHandler);
  }

  // Return cleanup function
  return () => {
    if (window.ethereum && window.ethereum.removeListener) {
      window.ethereum.removeListener('accountsChanged', accountsChangedHandler);
    }
    if (walletConnectProvider && walletConnectProvider.removeListener) {
      walletConnectProvider.removeListener('accountsChanged', accountsChangedHandler);
      walletConnectProvider.removeListener('disconnect', disconnectHandler);
    }
  };
};

// Listen for chain changes
// Note: WalletConnect chainChanged events are disabled due to internal library bugs
// that cause "setDefaultChain" and "request" errors on undefined providers
export const onChainChanged = (callback) => {
  const chainChangedHandler = (chainIdOrHex) => {
    try {
      // MetaMask sends hex, WalletConnect sends number
      const chainId = typeof chainIdOrHex === 'string'
        ? parseInt(chainIdOrHex, 16)
        : Number(chainIdOrHex);
      callback(chainId);
    } catch (error) {
      // Ignore errors during chain change handling
      console.warn('Chain change handler error (safe to ignore):', error.message);
    }
  };

  // MetaMask events only - WalletConnect chainChanged causes internal errors
  if (window.ethereum) {
    window.ethereum.on('chainChanged', chainChangedHandler);
  }

  // Don't register WalletConnect chainChanged listener - it causes internal errors:
  // "Cannot read properties of undefined (reading 'setDefaultChain')"
  // "Cannot read properties of undefined (reading 'request')"

  // Return cleanup function
  return () => {
    if (window.ethereum && window.ethereum.removeListener) {
      window.ethereum.removeListener('chainChanged', chainChangedHandler);
    }
  };
};

// Switch to a supported chain
export const switchChain = async (chainId) => {
  try {
    const chainConfig = SUPPORTED_CHAINS[chainId];
    if (!chainConfig) {
      throw new Error('Unsupported chain');
    }

    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${chainId.toString(16)}` }],
    });
  } catch (error) {
    // Chain not added to wallet, try to add it
    if (error.code === 4902) {
      await addChain(chainId);
    } else {
      throw error;
    }
  }
};

// Add chain to wallet
export const addChain = async (chainId) => {
  const chainConfig = SUPPORTED_CHAINS[chainId];
  if (!chainConfig) {
    throw new Error('Unsupported chain');
  }

  await window.ethereum.request({
    method: 'wallet_addEthereumChain',
    params: [{
      chainId: `0x${chainId.toString(16)}`,
      chainName: chainConfig.name,
      nativeCurrency: chainConfig.nativeCurrency,
      rpcUrls: [chainConfig.rpcUrl],
      blockExplorerUrls: [chainConfig.blockExplorer]
    }]
  });
};
